/**
 * Students Store - Zustand store for student management
 *
 * Provides student data with metadata (school, grade) for autocomplete search.
 * Supports adding new students on-the-fly from QuickAddModal.
 *
 * @module useStudentsStore
 */

import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { searchStudents, type SearchMatchResult } from "~/core/utils/hangulUtils";

// ============================================================================
// TYPES
// ============================================================================

export type StudentStatus = 'active' | 'paused' | 'waiting' | 'graduated' | 'quit';

export interface Student {
  id: string;
  name: string;
  school?: string;
  schoolId?: string;
  grade?: number; // 1-18 (초1-고3=1-12, N수=13, 대1-대4=14-17, 일반=18)
  phone?: string;
  parentPhone?: string;
  memo?: string;
  /** Permanent remarks (allergies, parent preferences, etc.) */
  remarks?: string;
  /** Flag for important remarks that need attention */
  isRemarkImportant?: boolean;
  status: StudentStatus;
  createdAt: Date;
  updatedAt: Date;
}

export interface StudentSearchResult extends Student {
  matchResult: SearchMatchResult;
}

interface StudentsState {
  students: Student[];
  isLoading: boolean;
  error: string | null;
  pendingStudentId: string | null;
}

interface StudentsActions {
  // ISSUE_167: 단순캐시 — 스토어는 React Query 캐시의 **읽기전용 미러**.
  // 모든 쓰기는 core/query/useStudents 의 RQ 뮤테이션을 거친다(직접변경 메서드 제거).

  // Search
  searchByName: (query: string) => StudentSearchResult[];
  getStudentById: (id: string) => Student | undefined;

  // 미러 투영 진입점 (useStudentsMirror 가 RQ data 로 1회 호출)
  setStudents: (students: Student[], isLoading?: boolean) => void;

  // Cross-module navigation
  setPendingStudentId: (id: string | null) => void;
}

type StudentsStore = StudentsState & StudentsActions;

// ============================================================================
// INITIAL DATA (Empty - populated from Supabase via useFetchStudents)
// ============================================================================

const INITIAL_STUDENTS: Student[] = [];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Format grade for display
 * @param grade - Grade number (1-17)
 * @returns Formatted string like "고2", "중3", "초6", "N수", "대1"
 */
export function formatGrade(grade: number | undefined): string {
  if (!grade) return "";

  if (grade <= 6) return `초${grade}`;
  if (grade <= 9) return `중${grade - 6}`;
  if (grade <= 12) return `고${grade - 9}`;
  if (grade === 13) return "N수";
  if (grade <= 17) return `대${grade - 13}`;
  if (grade === 18) return "일반";
  return "";
}

/**
 * School level derived from grade number
 */
export type SchoolLevel = "elem" | "mid" | "high" | "univ" | "general";

/**
 * Infer school level from school name suffix
 * 고려대학교→univ, 대성고→high, 청명중→mid, 한빛초→elem
 */
export function inferSchoolLevel(schoolName: string | undefined): SchoolLevel | null {
  if (!schoolName) return null;
  const name = schoolName.trim();
  if (/대학교?$|대$/.test(name)) return "univ";
  if (/고등학교$|고$/.test(name)) return "high";
  if (/중학교$|중$/.test(name)) return "mid";
  if (/초등학교$|초$/.test(name)) return "elem";
  return null;
}

/**
 * Get school level: school name first (explicit), grade fallback
 * "고려대학교" + 고3 → univ (학교 이름이 명시적이므로 우선)
 */
export function getSchoolLevel(grade: number | undefined, school?: string): SchoolLevel | null {
  const fromSchool = inferSchoolLevel(school);
  if (fromSchool) return fromSchool;
  if (grade) {
    if (grade <= 6) return "elem";
    if (grade <= 9) return "mid";
    if (grade <= 12) return "high";
    if (grade >= 13 && grade <= 17) return "univ";
    if (grade === 18) return "general";
  }
  return null;
}

/** School level display labels */
export const SCHOOL_LEVEL_LABELS: Record<SchoolLevel, string> = {
  elem: "초등",
  mid: "중등",
  high: "고등",
  univ: "대학",
  general: "일반",
};

/** Grade ranges per school level */
export const SCHOOL_LEVEL_GRADES: Record<SchoolLevel, number[]> = {
  elem: [1, 2, 3, 4, 5, 6],
  mid: [7, 8, 9],
  high: [10, 11, 12],
  univ: [13, 14, 15, 16, 17],
  general: [18],
};

/**
 * Format student metadata for display
 * @returns "[School] Grade" or just name if no metadata
 */
// 읽는 필드(school·grade)만 요구 — 전체 Student 뿐 아니라 검색결과 부분집합도 그대로 통과(any 제거).
export function formatStudentMeta(student: { school?: string; grade?: number }): string {
  const parts: string[] = [];

  if (student.school) {
    parts.push(student.school);
  }

  if (student.grade) {
    parts.push(formatGrade(student.grade));
  }

  return parts.length > 0 ? parts.join(" ") : "";
}

// ============================================================================
// STORE IMPLEMENTATION
// ============================================================================

export const useStudentsStore = create<StudentsStore>()(
  devtools(
    (set, get) => ({
      // State
      students: INITIAL_STUDENTS,
      isLoading: true,
      error: null,
      pendingStudentId: null,

      // Search students by name (supports Chosung)
      searchByName: (query) => {
        const { students } = get();
        return searchStudents(students, query);
      },

      // Get student by ID
      getStudentById: (id) => {
        return get().students.find((s) => s.id === id);
      },

      // Bulk set students (미러 투영 — RQ data 반영)
      setStudents: (students, isLoading = false) => {
        set({ students, isLoading }, false, "setStudents");
      },

      // Cross-module navigation
      setPendingStudentId: (id) => {
        set({ pendingStudentId: id }, false, "setPendingStudentId");
      },
    }),
    {
      name: "students-store",
      enabled: import.meta.env.DEV,
    }
  )
);

// ============================================================================
// SELECTOR HOOKS
// ============================================================================

/** Get all students */
export const useStudents = () => useStudentsStore((state) => state.students);

/** Get student count */
export const useStudentCount = () =>
  useStudentsStore((state) => state.students.length);

/** Search students (returns function to call with query) */
export const useStudentSearch = () =>
  useStudentsStore((state) => state.searchByName);

export default useStudentsStore;
